import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

import { FRAME_MEMBER_THICKNESS_METRES } from '../core/SceneRoot';
import type { RoomFloorplanDto } from '../generated/contracts';
import { buildLabelledPlate, disposePlate } from './LabelledPlate';
import type { Grabber, GrabberHub } from './Grabbers';

/**
 * The in-headset way to close the diagram a room is showing and return to the lobby (SPEC-200),
 * distinct from `ExitSign`'s own way to end the whole session.
 *
 * **Why a second sign rather than one sign with two meanings.** The owner, on the same request
 * that asked for this: *"Exiting the system must be available, but a way to close the existing
 * diagram and getting teleported back to the lobby to select another must also be possible."*
 * Both are real, both have to be reachable from inside a session, and — the part that makes them
 * genuinely distinct rather than two labels on one button — a wrong guess between them has
 * different stakes: ending the session drops this one person, while returning to the lobby
 * changes what *everyone* in the room is looking at (SPEC-161's own "the whole party moves
 * together" — `ShowDiagram`, C#, moves every connection in the room, not just this one). SPEC-073
 * already settled that `ExitSign` itself is not to be repurposed for this — considered and
 * refused by the owner — so this is a second fixture, not a second mode of the first one.
 *
 * **Why it looks like `ExitSign`.** "Match its aim-and-hold idiom so the two read as siblings"
 * (the brief this was built from) — the shared vocabulary is what makes the two fixtures legible
 * as a *pair* of related-but-different actions rather than two unrelated inventions bolted onto
 * the same wall. Same hold shape, same tolerances, same wall — the differences are only what each
 * one is labelled, what colour it is, and where on the wall it sits.
 *
 * **Where it sits, and why it can never collide with `ExitSign`.** Same north wall, same vertical
 * band below the name beam (`reposition()`'s own `y` derivation is copied from `ExitSign.ts`
 * verbatim, including its own worst-case-template reasoning), offset sideways by
 * `SIGN_CENTRE_OFFSET_METRES` from dead centre. `ExitSign` stays at `x = 0`; this sign sits at
 * `x = -SIGN_CENTRE_OFFSET_METRES`. The two signs' own half-widths
 * (`SIGN_WIDTH / 2` here, `0.25` for `ExitSign`'s identical `SIGN_WIDTH`) sum to 0.5m, and the
 * offset (0.65m) clears that with margin — proven as an inequality in this file's own test, not
 * merely picked and hoped for.
 *
 * **Only reachable while there is a diagram to close.** Wired to the same `idle` fact
 * `showLobbyIfIdle` already derives from the room's own diagram pointer (`mountRoomRoute.ts`) —
 * `setActive(!idle)` is the mirror image of `lobby.setEnabled(idle)`/`lobbyInput.setActive(idle)`.
 * A disabled sign both hides its mesh and stops arming a hold: aim-detection here is pure
 * arithmetic against the mesh's own world position, not a scene pick, so `TransformNode
 * .setEnabled(false)` alone would hide the plate without stopping it from still being aimable —
 * `active` is a second, explicit gate for exactly that reason.
 *
 * **What this class does and does not own.** Firing the hold calls `onReturnRequested` and
 * nothing else — the same "callback for a fact with nowhere else to live" shape `ExitSign
 * .onLeaveRequested` uses. `mountRoomRoute.ts` wires it to release whatever this client is
 * holding (the same defensive release `ExitSign`'s own wiring performs, and for the identical
 * reason: `ShowDiagram`'s own server-side lock release happens as part of the mutation, but a
 * client that was mid-grab still has stale possession state referencing a mesh the new,
 * diagram-less snapshot will not rebuild) and then call `ShowDiagram(roomId, null)` through the
 * hub — never assigned at all for a spectator, who has no write access to change what the room
 * shows (`RoomSyncHub.ShowDiagram`'s own spectator refusal) and for whom this sign, ticking
 * unconditionally like `ExitSign`'s own, would otherwise arm a hold that fires into nothing.
 */

/** Matches `ExitSign.ts`'s and `WhiteboardLayout.ts`'s own identical constant and reasoning. */
const WALL_THICKNESS = 0.1;

/** Matches `ExitSign.ts`'s own identical constant. */
const WALL_CLEARANCE = 0.01;

/** Matches `ExitSign.ts`'s own identical constant and derivation — see that file's own module
 *  comment for the full worst-case-template accounting this figure rests on. */
const SIGN_GAP_BELOW_BEAM_METRES = 0.03;

const SIGN_WIDTH = 0.55;
const SIGN_HEIGHT = 0.12;

/**
 * How far this sign's own centre sits from dead centre of the wall (where `ExitSign` mounts),
 * so the two plates can never overlap regardless of which is built or reposition()'d first.
 * `SIGN_WIDTH / 2` (0.275) plus `ExitSign`'s own identical half-width (0.25) is 0.525 — the
 * minimum separation that clears both plates edge-to-edge; 0.65 leaves a visible gap between them
 * rather than merely avoiding an exact touch.
 */
const SIGN_CENTRE_OFFSET_METRES = 0.65;

/** Matches `ExitSign.ts`'s own identical tolerances — proven reachable from across a real room by
 *  `DebugPanel`'s own aim tests, which both signs' idiom is copied from. */
const AIM_TOLERANCE = 0.12;
const MIN_AIM_DISTANCE = 0.15;
const MAX_AIM_DISTANCE = 8;

/**
 * Same duration as `ExitSign.ts`'s own hold — closing a diagram is not as severe as ending the
 * whole session, but it is still a change every person in the room feels (SPEC-161's "the whole
 * party moves together"), so it gets the identical deliberation, not a shorter one.
 */
const HOLD_MS = 900;

/** Matches `ExitSign.ts`'s own identical constant and reasoning. */
const HOLD_MAX_SCALE_BUMP = 0.35;

const LABEL = 'return to lobby';

/** A cooler, navigational colour — deliberately not `ExitSign`'s pink, which reads as "this ends
 *  something." Returning to the lobby is a place to go, not an ending. */
const BACKGROUND = '#5FD9C0';
const INK = '#12151A';

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// spec: SPEC-200
export class LobbyReturnSign {
  private readonly root: TransformNode;
  private readonly mesh: Mesh;

  /** Mirrors `ExitSign.positioned` — set once `reposition()` has run, so `tick()` has real
   *  geometry to test aim against. */
  private positioned = false;

  /** Whether the sign is currently the interactive surface at all — false whenever the room has
   *  no diagram open to close (`setActive`'s own doc comment). Distinct from `positioned`: a sign
   *  can be positioned (its wall placement is known) and still inactive (nothing to return from
   *  right now). */
  private active = false;

  /** Mirrors `ExitSign.holdFill` — see that field's own doc for the "one owner at a time" shape
   *  this copies verbatim. */
  private holdFill: { grabberId: string; since: number } | null = null;

  /** Fired once the hold completes. See this file's own module comment for what
   *  `mountRoomRoute.ts` wires this to and why this class calls neither the hub nor
   *  `PossessionManager` itself. */
  onReturnRequested: (() => void) | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly grabbers: GrabberHub,
  ) {
    this.root = new TransformNode('lobby-return-sign', scene);
    this.root.setEnabled(false);

    this.mesh = buildLabelledPlate(scene, 'lobby-return-sign-plate', LABEL, BACKGROUND, INK, SIGN_WIDTH, SIGN_HEIGHT);
    this.mesh.parent = this.root;
    this.mesh.isPickable = false;
  }

  /** Whether the sign is currently shown and interactive — both `positioned` and `active`. */
  get isVisible(): boolean {
    return this.positioned && this.active;
  }

  /**
   * Places the sign on the north wall for a room of this shape. Safe to call repeatedly, the
   * same "call once after the first `buildRoom()`, again on every resync" treatment
   * `ExitSign.reposition`/`DebugPanel.reposition` already get — does not by itself make the sign
   * visible; `setActive(true)` is what does that, since a resync can land while a diagram is
   * still open or while the room is idle, and this call alone knows only the room's shape, not
   * which of those is currently true.
   *
   * See this file's own module comment for the `SIGN_CENTRE_OFFSET_METRES` derivation that keeps
   * this mount from ever overlapping `ExitSign`'s.
   */
  reposition(floorplan: RoomFloorplanDto): void {
    const halfDepth = floorplan.depth / 2;
    const wallInnerZ = halfDepth - WALL_THICKNESS / 2;
    const mountZ = wallInnerZ - WALL_CLEARANCE;

    const beamBottomY = floorplan.wallHeight - FRAME_MEMBER_THICKNESS_METRES;
    const y = clampNumber(
      beamBottomY - SIGN_GAP_BELOW_BEAM_METRES - SIGN_HEIGHT / 2,
      SIGN_HEIGHT / 2,
      floorplan.wallHeight - SIGN_HEIGHT / 2,
    );

    this.root.position.set(-SIGN_CENTRE_OFFSET_METRES, y, mountZ);
    this.positioned = true;
    this.root.setEnabled(this.active);
  }

  /**
   * Whether the room currently has a diagram open to close — the mirror image of
   * `lobby.setEnabled(idle)`/`lobbyInput.setActive(idle)` in `mountRoomRoute.ts`, driven from the
   * identical fact so the three can never disagree about which state the room is in. Both hides
   * the mesh and stops `tick()` from arming a hold — see this file's own module comment for why
   * `setEnabled(false)` alone would not be enough.
   */
  setActive(active: boolean): void {
    this.active = active;
    this.root.setEnabled(active && this.positioned);

    if (!active) {
      this.mesh.scaling.setAll(1);
      this.holdFill = null;
    }
  }

  get isActive(): boolean {
    return this.active;
  }

  /** Mirrors `ExitSign.tick` exactly, with one addition: an inactive sign arms no hold at all,
   *  regardless of aim. */
  private tick(nowMs: number): void {
    if (!this.positioned || !this.active) {
      return;
    }

    let holderStillAiming = false;

    for (const grabber of this.grabbers.grabbers) {
      if (!this.isAimedAt(grabber) || !grabber.far.pressed) {
        continue;
      }

      if (this.holdFill !== null && this.holdFill.grabberId === grabber.id) {
        holderStillAiming = true;
      } else if (this.holdFill === null) {
        this.holdFill = { grabberId: grabber.id, since: nowMs };
        holderStillAiming = true;
      } else {
        continue;
      }

      const progress = Math.min(1, (nowMs - this.holdFill.since) / HOLD_MS);
      this.mesh.scaling.setAll(1 + progress * HOLD_MAX_SCALE_BUMP);

      if (progress >= 1) {
        this.holdFill = null;
        this.mesh.scaling.setAll(1);
        this.onReturnRequested?.();
      }
    }

    if (!holderStillAiming && this.holdFill !== null) {
      this.mesh.scaling.setAll(1);
      this.holdFill = null;
    }
  }

  /** Registered the same way every other per-frame gesture reader in this project is
   *  (`ExitSign.start`, `DebugPanel.start`) — returns the teardown. */
  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.tick(performance.now()));
    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  /** Mirrors `ExitSign.isAimedAt` exactly — see that method's own doc for the forced
   *  `computeWorldMatrix` reasoning (CLAUDE.md's "unforced can return last frame's answer"). */
  private isAimedAt(grabber: Grabber): boolean {
    this.mesh.computeWorldMatrix(true);
    const position = this.mesh.getAbsolutePosition();

    const toSign = position.subtract(grabber.aimOrigin);
    const along = Vector3.Dot(toSign, grabber.forward);

    if (along < MIN_AIM_DISTANCE || along > MAX_AIM_DISTANCE) {
      return false;
    }

    const closestOnRay = grabber.aimOrigin.add(grabber.forward.scale(along));
    return Vector3.Distance(closestOnRay, position) <= AIM_TOLERANCE;
  }

  /** Frees the mesh's own material and texture along with itself (`LabelledPlate.disposePlate`),
   *  then the now-childless root — the identical pattern `ExitSign.dispose` uses. */
  dispose(): void {
    disposePlate(this.mesh);
    this.root.dispose();
  }
}
